import { Alert, Box, FormHelperText, FormLabel, Grid, MenuItem, Stack } from '@mui/material'
import type { GeneratorInput } from '../types/GeneratorInput'
import { FormikErrors, FormikHandlers, FormikHelpers, FormikTouched, getIn } from 'formik'
import { useTranslation } from 'react-i18next'
import GeneratorRelayThresholdField from './GeneratorRelayThresholdField'
import GeneratorRelayThresholdSlider from './GeneratorRelayThresholdSlider'
import { roundValue } from '../utils/string'
import GeneratorAdditionalLoadField from './GeneratorAdditionalLoadField'
import { PowerUnit } from '../enums/PowerUnit'
import { usePolicy } from '../contexts/PolicyContext'
import { LoadAutomationMode } from '../types/Generator'
import {
  convertKilovoltAmperesToAmperes,
  convertKilovoltAmperesToKilowatts
} from '../utils/convert'

export interface GeneratorRelayThresholdFieldsProps {
  values: GeneratorInput
  errors: FormikErrors<GeneratorInput>
  touched: FormikTouched<GeneratorInput>
  isSubmitting: boolean
  actualLoadAutomationMode?: LoadAutomationMode
  setFieldValue: FormikHelpers<GeneratorInput>['setFieldValue']
  handleBlur: FormikHandlers['handleBlur']
}

export default function GeneratorRelayThresholdFields({
  values,
  errors,
  touched,
  isSubmitting,
  actualLoadAutomationMode,
  setFieldValue,
  handleBlur
}: GeneratorRelayThresholdFieldsProps) {
  const { t } = useTranslation('generator')
  const { isSuperadmin } = usePolicy()

  const apparentPower = values.apparentPower
  const nominalVoltage = values.nominalVoltage
  const relayControlMode = values.loadAutomationMode ?? LoadAutomationMode.LOCAL
  const isCloudAutomationMode = actualLoadAutomationMode === LoadAutomationMode.CLOUD
  const additionalLoadUnit = values.additionalLoadUnit ?? PowerUnit.KILOWATT
  const additionalLoad = values.additionalLoad ?? 0
  const additionalLoadThreshold =
    additionalLoadUnit === PowerUnit.KILOWATT
      ? (additionalLoad / convertKilovoltAmperesToKilowatts(apparentPower, 1)) * 100
      : (additionalLoad / convertKilovoltAmperesToAmperes(apparentPower, nominalVoltage)) * 100

  const additionalLoadThresholdKva = !isNaN(additionalLoadThreshold)
    ? (apparentPower * additionalLoadThreshold) / 100
    : undefined

  const relayOffThresholdKva =
    values.relayOffThreshold != null ? (apparentPower * values.relayOffThreshold) / 100 : undefined
  const relayOnThresholdKva =
    values.relayOnThreshold != null ? (apparentPower * values.relayOnThreshold) / 100 : undefined

  function isError(field: string) {
    const fieldTouched = getIn(touched, field)
    const fieldErrors = getIn(errors, field)

    return Boolean(fieldTouched && fieldErrors)
  }

  function getHelperText(field: string) {
    const fieldTouched = getIn(touched, field)
    const fieldErrors = getIn(errors, field)

    return fieldTouched ? fieldErrors : undefined
  }

  function handleNumberFieldChange(field: keyof GeneratorInput) {
    return (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = event.target

      setFieldValue(field as string, value === '' ? undefined : Number(value))
    }
  }

  return (
    <Grid container spacing={6}>
      <Grid item xs={12} marginTop={2}>
        <GeneratorAdditionalLoadField
          name="additionalLoad"
          value={values.additionalLoad}
          unit={additionalLoadUnit}
          disabled={isSubmitting}
          error={isError('additionalLoad')}
          helperText={getHelperText('additionalLoad')}
          fullWidth
          onBlur={handleBlur}
          onChange={handleNumberFieldChange('additionalLoad')}
          onUnitChange={(unit) => {
            setFieldValue('additionalLoadUnit', unit)
          }}
        />

        {!isNaN(additionalLoadThreshold) && (
          <Alert severity="info" variant="outlined" sx={{ mt: 2, border: 0, px: 0, py: 0 }}>
            {t('additionLoadIsOfApparentPower', {
              value: roundValue(additionalLoadThreshold, 1)
            })}
            {additionalLoadThresholdKva !== undefined &&
              ` (${roundValue(additionalLoadThresholdKva, 2)} kVA)`}
          </Alert>
        )}
      </Grid>

      <Grid item xs={12}>
        <FormLabel>{t('relayControlThresholds')}</FormLabel>
      </Grid>

      <Grid item xs={12} sm={6}>
        <GeneratorRelayThresholdField
          select
          name="loadAutomationMode"
          label={t('relayControlMode')}
          value={relayControlMode}
          error={isError('loadAutomationMode')}
          helperText={getHelperText('loadAutomationMode')}
          disabled={isSubmitting || (!isSuperadmin && isCloudAutomationMode)}
          fullWidth
          onBlur={handleBlur}
          onChange={(event) => {
            const mode = event.target.value as LoadAutomationMode
            setFieldValue('loadAutomationMode', mode)
          }}
        >
          <MenuItem value={LoadAutomationMode.MANUAL}>{t('relayControlModeManual')}</MenuItem>
          {isSuperadmin && (
            <MenuItem value={LoadAutomationMode.CLOUD}>{t('relayControlModeCloud')}</MenuItem>
          )}
          <MenuItem value={LoadAutomationMode.LOCAL}>{t('automation:automation')}</MenuItem>
        </GeneratorRelayThresholdField>
      </Grid>

      <Grid item xs={12} sm={6} />

      <Grid item xs={12} sm={6}>
        <GeneratorRelayThresholdField
          name="relayOnThreshold"
          label={`${t('relayOnThreshold')} (%)`}
          value={values.relayOnThreshold ?? undefined}
          error={isError('relayOnThreshold')}
          helperText={
            getHelperText('relayOnThreshold') ||
            (relayOnThresholdKva !== undefined
              ? t('thresholdKvaValue', { value: roundValue(relayOnThresholdKva, 2) })
              : undefined)
          }
          disabled={isSubmitting}
          fullWidth
          onBlur={handleBlur}
          onChange={handleNumberFieldChange('relayOnThreshold')}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <GeneratorRelayThresholdField
          name="relayOffThreshold"
          label={`${t('relayOffThreshold')} (%)`}
          value={values.relayOffThreshold ?? undefined}
          error={isError('relayOffThreshold')}
          helperText={
            getHelperText('relayOffThreshold') ||
            (relayOffThresholdKva !== undefined
              ? t('thresholdKvaValue', { value: roundValue(relayOffThresholdKva, 2) })
              : undefined)
          }
          disabled={isSubmitting}
          fullWidth
          onBlur={handleBlur}
          onChange={handleNumberFieldChange('relayOffThreshold')}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <GeneratorRelayThresholdField
          name="relayOnDelay"
          label={`${t('relayOnDelay')} (s)`}
          value={values.relayOnDelay ?? undefined}
          error={isError('relayOnDelay')}
          helperText={getHelperText('relayOnDelay')}
          disabled={isSubmitting}
          fullWidth
          onBlur={handleBlur}
          onChange={handleNumberFieldChange('relayOnDelay')}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <GeneratorRelayThresholdField
          name="relayOffDelay"
          label={`${t('relayOffDelay')} (s)`}
          value={values.relayOffDelay ?? undefined}
          error={isError('relayOffDelay')}
          helperText={getHelperText('relayOffDelay')}
          disabled={isSubmitting}
          fullWidth
          onBlur={handleBlur}
          onChange={handleNumberFieldChange('relayOffDelay')}
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ px: 4 }}>
          <GeneratorRelayThresholdSlider
            value={[values.relayOnThreshold ?? 0, values.relayOffThreshold ?? 100]}
            minDistance={additionalLoadThreshold ?? undefined}
            onChange={(_, thresholds) => {
              if (Array.isArray(thresholds) && thresholds.length === 2) {
                setFieldValue('relayOnThreshold', thresholds[0])
                setFieldValue('relayOffThreshold', thresholds[1])
              }
            }}
          />
        </Box>

        <Stack direction="row" justifyContent="space-between" gap={2}>
          {values.relayOnThreshold != undefined && (
            <FormHelperText>
              {t('relayIsTurnedOnWhenPowerDropsBelow', {
                threshold: `${roundValue(values.relayOnThreshold, 2)}% (${roundValue(
                  relayOnThresholdKva ?? 0,
                  2
                )} kVA)`
              })}
            </FormHelperText>
          )}

          {values.relayOffThreshold != undefined && (
            <FormHelperText>
              {t('relayIsTurnedOffWhenPowerRisesAbove', {
                threshold: `${roundValue(values.relayOffThreshold, 2)}% (${roundValue(
                  relayOffThresholdKva ?? 0,
                  2
                )} kVA)`
              })}
            </FormHelperText>
          )}
        </Stack>
      </Grid>
    </Grid>
  )
}
